/**
 * De onde o front tira o token.
 *
 * Fluxo normal: sessionStorage (some ao fechar a aba).
 * "Manter conectado": localStorage (persiste entre visitas). Trade-off
 * consciente de XSS por mais tempo, pedido pelo produto no login.
 */

export type FonteDeToken = () => string | null;

export type OpcoesDePersistenciaDoToken = {
  readonly manterConectado?: boolean;
};

export type ControleDeToken = {
  readonly tokenAtual: FonteDeToken;
  readonly definirToken: (
    novo: string | null,
    opcoes?: OpcoesDePersistenciaDoToken,
  ) => void;
  readonly manterConectadoAtual: () => boolean;
  /**
   * Quando o token atual foi gravado (ms). Serve para renovar a sessão de quem
   * marcou "Manter conectado" antes de ela vencer no servidor.
   */
  readonly gravadoEm: () => number | null;
};

export function tokenDoAmbiente(): string | null {
  const valor = import.meta.env.VITE_TOKEN_DA_API;
  return typeof valor === "string" && valor.length > 0 ? valor : null;
}

/** Guarda em memoria; sobrevive a navegacao SPA, nao a recarga da pagina. */
export function fonteDeTokenEmMemoria(
  inicial: string | null = null,
): ControleDeToken {
  let token = inicial;
  let manter = false;
  return {
    tokenAtual: (): string | null => token,
    definirToken: (novo, opcoes): void => {
      token = novo;
      if (opcoes?.manterConectado !== undefined) {
        manter = opcoes.manterConectado;
      }
      if (novo === null) manter = false;
    },
    manterConectadoAtual: (): boolean => manter,
    gravadoEm: (): number | null => null,
  };
}

const CHAVE_SESSAO = "renke-refs.acesso";
const CHAVE_MANTER = "renke-refs.manter-conectado";
const CHAVE_GRAVADO_EM = "renke-refs.token-gravado-em";

type StorageMinimo = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function ler(storage: StorageMinimo | undefined, chave: string): string | null {
  if (!storage) return null;
  try {
    const valor = storage.getItem(chave);
    return valor && valor.length > 0 ? valor : null;
  } catch {
    return null;
  }
}

function gravar(
  storage: StorageMinimo | undefined,
  chave: string,
  valor: string | null,
): void {
  if (!storage) return;
  try {
    if (valor === null || valor.length === 0) {
      storage.removeItem(chave);
      return;
    }
    storage.setItem(chave, valor);
  } catch {
    /* storage indisponivel */
  }
}

/**
 * Persiste o token em sessionStorage (morre ao fechar a aba).
 * Ignora "manter conectado" — so a fonte do navegador usa localStorage.
 */
export function fonteDeTokenEmSessao(
  armazenamento: StorageMinimo = globalThis.sessionStorage,
): ControleDeToken {
  return {
    tokenAtual: (): string | null => ler(armazenamento, CHAVE_SESSAO),
    definirToken: (novo): void => {
      gravar(armazenamento, CHAVE_SESSAO, novo);
    },
    manterConectadoAtual: (): boolean => false,
    gravadoEm: (): number | null => null,
  };
}

/**
 * sessionStorage por padrao; localStorage quando "Manter conectado" esta ativo.
 */
export function fonteDeTokenDoNavegador(
  sessao: StorageMinimo = globalThis.sessionStorage,
  persistente: StorageMinimo = globalThis.localStorage,
  agora: () => number = () => Date.now(),
): ControleDeToken {
  return {
    tokenAtual: (): string | null => {
      const longo = ler(persistente, CHAVE_SESSAO);
      if (longo) return longo;
      return ler(sessao, CHAVE_SESSAO);
    },
    definirToken: (novo, opcoes): void => {
      if (novo === null || novo.length === 0) {
        gravar(sessao, CHAVE_SESSAO, null);
        gravar(persistente, CHAVE_SESSAO, null);
        gravar(persistente, CHAVE_MANTER, null);
        gravar(persistente, CHAVE_GRAVADO_EM, null);
        return;
      }

      const manter =
        opcoes?.manterConectado ??
        ler(persistente, CHAVE_MANTER) === "1";

      if (manter) {
        gravar(persistente, CHAVE_SESSAO, novo);
        gravar(persistente, CHAVE_MANTER, "1");
        gravar(persistente, CHAVE_GRAVADO_EM, String(agora()));
        gravar(sessao, CHAVE_SESSAO, null);
        return;
      }

      gravar(sessao, CHAVE_SESSAO, novo);
      gravar(persistente, CHAVE_SESSAO, null);
      gravar(persistente, CHAVE_MANTER, null);
      gravar(persistente, CHAVE_GRAVADO_EM, null);
    },
    manterConectadoAtual: (): boolean => ler(persistente, CHAVE_MANTER) === "1",
    gravadoEm: (): number | null => {
      const bruto = Number(ler(persistente, CHAVE_GRAVADO_EM));
      return Number.isFinite(bruto) && bruto > 0 ? bruto : null;
    },
  };
}
